'use client'

import React from 'react'

import { Building2, ChevronRight, MapPin } from 'lucide-react'
import Link from 'next/link'

import type { RecipientProfile } from '@/types/usaspending'

import { DistributionBar } from '../DistributionBar'
import { Currency, DateDisplay, NumberDisplay } from '../Formatters'

export interface RecipientViewProps {
  recipient: RecipientProfile
}

/**
 * Recipient View Component
 *
 * Displays company/recipient profile with stats, charts, and recent awards.
 * Refined with professional polish and strong visual hierarchy.
 */
export const RecipientView: React.FC<RecipientViewProps> = ({ recipient }) => {
  const { stats, topAgencies, topPscCodes, recentAwards, location } = recipient

  return (
    <div className="flex min-h-full w-full flex-1 flex-col items-center bg-primary-50 pb-12 dark:bg-primary-950">
      {/* Header */}
      <header className="w-full border-b border-primary-200 bg-white dark:border-primary-800 dark:bg-primary-950">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
          {/* Breadcrumbs */}
          <nav className="mb-6 flex items-center gap-2 text-xs font-medium text-primary-500 dark:text-primary-400">
            <Link href="/" className="transition-colors hover:text-accent-500">
              Home
            </Link>
            <ChevronRight className="h-3.5 w-3.5 text-primary-300 dark:text-primary-600" />
            <Link
              href="/recipients/search"
              className="transition-colors hover:text-accent-500"
            >
              Recipients
            </Link>
            <ChevronRight className="h-3.5 w-3.5 text-primary-300 dark:text-primary-600" />
            <span className="font-semibold text-primary-900 dark:text-primary-100">
              {recipient.name}
            </span>
          </nav>

          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-primary-200 bg-primary-50 dark:border-primary-700 dark:bg-primary-800">
              <Building2 className="h-7 w-7 text-primary-400 dark:text-primary-500" />
            </div>
            <div className="flex-1">
              <h1 className="mb-2 text-3xl font-bold leading-tight tracking-tight text-primary-900 dark:text-primary-50 md:text-4xl">
                {recipient.name}
              </h1>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium text-primary-600 dark:text-primary-400">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-normal uppercase tracking-wider text-primary-400 dark:text-primary-500">
                    UEI
                  </span>
                  <span className="rounded border border-primary-200 bg-primary-100 px-2 py-0.5 font-mono text-primary-800 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-200">
                    {recipient.uei}
                  </span>
                </div>
                {recipient.duns && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-normal uppercase tracking-wider text-primary-400 dark:text-primary-500">
                      DUNS
                    </span>
                    <span className="rounded border border-primary-200 bg-primary-100 px-2 py-0.5 font-mono text-primary-800 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-200">
                      {recipient.duns}
                    </span>
                  </div>
                )}
                <span className="flex items-center gap-1 text-primary-500 dark:text-primary-400">
                  <MapPin className="h-3.5 w-3.5" />
                  {location.city}, {location.state}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl space-y-6 px-4 py-8 md:px-8">
        {/* Stats Cards */}
        {stats && (
          <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <StatCard
              label="Total Obligated"
              value={<Currency value={stats.totalObligatedAmount} />}
            />
            <StatCard
              label="Prime Awards"
              value={<NumberDisplay value={stats.totalPrimeAwards} />}
            />
            <StatCard
              label="Last Transaction"
              value={<DateDisplay dateStr={stats.lastTransactionDate} />}
            />
          </section>
        )}

        {/* Charts Row */}
        <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-700 dark:bg-primary-900">
            <DistributionBar
              items={topAgencies}
              title="Top 5 Awarding Agencies"
              showAmount
            />
          </div>
          <div className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-700 dark:bg-primary-900">
            <DistributionBar
              items={topPscCodes}
              title="Top 5 Product Service Codes"
              showAmount
            />
          </div>
        </section>

        {/* Recent Awards Table */}
        <section className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-700 dark:bg-primary-900">
          <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-primary-900 dark:text-primary-100">
            Recent Prime Awards
          </h3>
          <div className="overflow-hidden rounded-lg border border-primary-200 dark:border-primary-700">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-primary-200 bg-primary-50 text-xs font-semibold uppercase text-primary-500 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-400">
                <tr>
                  <th className="px-4 py-3">Award ID</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Agency</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-primary-100 dark:divide-primary-800">
                {recentAwards.map((award) => (
                  <tr
                    key={award.id}
                    className="bg-white transition-colors hover:bg-primary-50 dark:bg-primary-900 dark:hover:bg-primary-800"
                  >
                    <td className="px-4 py-3">
                      <Link
                        href={`/awards/usaspending/${award.id}`}
                        className="font-mono font-medium text-accent-500 hover:underline dark:text-accent-400"
                      >
                        {award.piid}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-primary-600 dark:text-primary-400">
                      <DateDisplay dateStr={award.date} />
                    </td>
                    <td className="px-4 py-3 text-primary-600 dark:text-primary-400">
                      {award.agency}
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded border border-primary-200 bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-300">
                        {award.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums text-primary-900 dark:text-primary-100">
                      <Currency value={award.amount} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  )
}

interface StatCardProps {
  label: string
  value: React.ReactNode
}

const StatCard: React.FC<StatCardProps> = ({ label, value }) => (
  <div className="rounded-xl border border-primary-200 bg-white p-5 shadow-sm dark:border-primary-700 dark:bg-primary-900">
    <div className="mb-2 text-xs font-bold uppercase tracking-wider text-primary-500 dark:text-primary-400">
      {label}
    </div>
    <div className="text-2xl font-semibold tabular-nums text-primary-900 dark:text-primary-100">
      {value}
    </div>
  </div>
)
